// Cliente HTTP contra el backend, con el manejo de sesión que necesita cualquier pantalla.
//
// Este `request` venía copiado en cuatro archivos (reservationsApi, collectionsApi, mlApi,
// imageApi) y el encabezado de `imageApi.js` ya anotaba que extraerlo era un follow-up. Acá
// está extraído, y lo usa `tvApi.js`. Los cuatro originales NO se tocan: migrarlos es un
// cambio que toca pantallas que no tienen nada que ver con la cartelera, y este archivo deja
// el camino hecho para cuando alguna de ellas se toque por otro motivo.
//
// Lo que no es obvio y por eso vive acá adentro:
//
//   - El backend responde los errores con HTTP 200 y `{error, message}` en el cuerpo
//     (convención del repo), así que `res.ok` NO alcanza para saber si algo salió bien.
//   - El 1002 tiene que cerrar la sesión. Sin `endSession()`, el admin se queda en una
//     pantalla que ya no puede guardar nada, viendo "La sesión expiró" una y otra vez sin que
//     nadie lo mande al login.

import { endSession } from './session';

const config = process.env;

export const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
const apiUrl = (path) => apiBase() + path;

export const authHeaders = () => ({ 'access-token': localStorage.getItem('token') });

const SESSION_EXPIRED = 'La sesión expiró.';
const FORBIDDEN = 'Tu usuario no tiene permiso para esto en la sucursal seleccionada.';
const UNKNOWN_SESSION = 'No pudimos verificar tu sesión. Recargá la página y, si sigue, volvé a entrar.';

let probeInFlight = null;

async function probeSessionAlive() {
    const token = localStorage.getItem('token');
    if (!token) return false;
    if (probeInFlight == null) {
        probeInFlight = (async () => {
            try {
                const res = await fetch(`${apiBase()}session/checkSessionAdmin`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({}),
                });
                const body = await res.json();
                if (body && body.data && typeof body.data.isValidToken === 'boolean') return body.data.isValidToken;
                return null;
            } catch (e) {
                return null;   // no se pudo averiguar: NO se destruye nada
            } finally {
                setTimeout(() => { probeInFlight = null; }, 0);
            }
        })();
    }
    return probeInFlight;
}

export async function request(path, options = {}) {
    let res;
    try {
        res = await fetch(apiUrl(path), {
            ...options,
            headers: { ...authHeaders(), ...(options.headers || {}) },
        });
    } catch (networkError) {
        const err = new Error('No pudimos hablar con el servidor. Revisá la conexión y reintentá.');
        err.code = 0;
        throw err;
    }

    let body = null;
    try {
        body = await res.json();
    } catch (parseError) {
        body = null;
    }

    const code = body && Number(body.error) > 0 ? Number(body.error) : 0;

    if (code === 1002) {
        // Un 1002 NO prueba que la sesión venció: `enpoints.js` es fail-closed y devuelve lo
        // mismo cuando la ruta no está registrada o el rol no alcanza. Desloguear sin verificar
        // echaba al admin por una ruta nueva mal declarada. Se pregunta primero.
        const alive = await probeSessionAlive();
        const err = new Error(alive === true ? FORBIDDEN : alive === false ? SESSION_EXPIRED : UNKNOWN_SESSION);
        err.code = 1002;
        if (alive === false) endSession();
        throw err;
    }

    if (!res.ok || code) {
        // El motivo de negocio viaja anidado en data.message (ENI-116/ENI-210); el message de
        // primer nivel es genérico por código.
        const msg = (body && body.data && body.data.message) || (body && body.message)
            || 'No se pudo completar la operación';
        const err = new Error(msg);
        err.code = code;
        err.body = body;
        throw err;
    }

    return body ? body.data : null;
}

export const get = (path) => request(path, { method: 'GET' });

export const del = (path) => request(path, { method: 'DELETE' });

const conCuerpo = (metodo) => (path, data) => request(path, {
    method: metodo,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data || {}),
});

export const post = conCuerpo('POST');
export const put = conCuerpo('PUT');
export const patch = conCuerpo('PATCH');
// DELETE con cuerpo: para las bajas que piden el nombre de quien las hace.
export const delConCuerpo = conCuerpo('DELETE');

// Sube un Blob/File como multipart. OJO: no se setea 'Content-Type' — con un body `FormData`
// lo tiene que poner el navegador, que es el único que conoce el `boundary`.
export function upload(path, blob, filename) {
    const form = new FormData();
    form.append('image', blob, filename || 'imagen');
    return request(path, { method: 'POST', body: form });
}
